import { IS_IN_BROWSER } from '@bob/utils/browser'
import * as shopify from './base'
import { request, fragment, query, gqlToQuery } from './requester'

type ResponsiveImage = shopify.Image & {
    transformedSrc1x: string | null
    transformedSrc2x: string | null
    transformedSrc3x: string | null
}

export type Variant = {
    availableForSale: boolean
    currentlyNotInStock: boolean
    compareAtPriceV2: shopify.Money | null
    id: string
    image: ResponsiveImage
    priceV2: shopify.Money
    quantityAvailable: number | null
    requiresShipping: boolean
    sku: string | null
    title: string
    selectedOptions: { name: string; value: string }[]
}

const VARIANT_FRAGMENT = fragment`
    fragment BaseVariant on ProductVariant {
        availableForSale
        currentlyNotInStock
        compareAtPriceV2 {
            ...${shopify.MONEY_FRAGMENT}
        }
        id
        image {
            ...${shopify.IMAGE_FRAGMENT}
            transformedSrc1x: url(transform: {maxWidth: 550, scale: 1})
            transformedSrc2x: url(transform: {maxWidth: 550, scale: 2})
            transformedSrc3x: url(transform: {maxWidth: 550, scale: 2})

        }
        priceV2 {
            ...${shopify.MONEY_FRAGMENT}
        }
        quantityAvailable
        requiresShipping
        sku
        title
        selectedOptions {
            name
            value
        }
    }
`

export type Recommendation = {
    handle: string
    id: string
    images: {
        edges: {
            node: ResponsiveImage
        }[]
    }
    priceRange: {
        maxVariantPrice: shopify.Money
        minVariantPrice: shopify.Money
    }
    title: string
}
const GET_RECOMMENDATIONS_QUERY = query`
    query($id: ID!) {
        productRecommendations(productId: $id) {
            handle
            id
            images(first: 10) {
                edges {
                    cursor
                    node {
                        ...${shopify.IMAGE_FRAGMENT}
                        transformedSrc1x: url(transform: {maxWidth: 300, scale: 1})
                        transformedSrc2x: url(transform: {maxWidth: 300, scale: 2})
                        transformedSrc3x: url(transform: {maxWidth: 300, scale: 2})
                    }
                }
            }
            priceRange {
                maxVariantPrice {
                    ...${shopify.MONEY_FRAGMENT}
                }
                minVariantPrice {
                    ...${shopify.MONEY_FRAGMENT}
                }
            }
            title
        }
    }
`

export async function getRecommendations(id: string): Promise<Recommendation[]> {
    const result = await request(
        GET_RECOMMENDATIONS_QUERY,
        {
            id
        },
        IS_IN_BROWSER
    )

    return result.productRecommendations
}

export type PartialProduct = {
    handle: string
    id: string
    title: string
}

export const PARTIAL_PRODUCT_FRAGMENT = fragment`
    fragment PartialProduct on Product {
        handle
        id
        title
    }
`

export type MediumProduct = PartialProduct & {
    availableForSale: boolean
    brandName: {
        value: string | null
    } | null
    compareAtPriceRange: {
        maxVariantPrice: shopify.Money
        minVariantPrice: shopify.Money
    }
    descriptionHtml: string
    images: { edges: { node: ResponsiveImage }[] }
    priceRange: {
        maxVariantPrice: shopify.Money
        minVariantPrice: shopify.Money
    }
    tags: string[]
    productType: string
    vendor: string
}

export const MEDIUM_PRODUCT_FRAGMENT = fragment`
    fragment MediumProduct on Product {
        ...${PARTIAL_PRODUCT_FRAGMENT}
        availableForSale
        brandName: metafield(namespace: "api", key: "brand_name") {
            value
        }
        compareAtPriceRange {
            maxVariantPrice {
                ...${shopify.MONEY_FRAGMENT}
            }
            minVariantPrice {
                ...${shopify.MONEY_FRAGMENT}
            }
        }
        images(first: 150) {
            edges {
                cursor
                node {
                    ...${shopify.IMAGE_FRAGMENT}
                    transformedSrc1x: url(transform: {maxWidth: 550, scale: 1})
                    transformedSrc2x: url(transform: {maxWidth: 550, scale: 2})
                    transformedSrc3x: url(transform: {maxWidth: 550, scale: 2})
                }
            }
        }
        descriptionHtml
        tags
        priceRange {
            maxVariantPrice {
                ...${shopify.MONEY_FRAGMENT}
            }
            minVariantPrice {
                ...${shopify.MONEY_FRAGMENT}
            }
        }
        productType
        vendor
    }
`

export type Product = MediumProduct & {
    deliveryExtra: {
        value: string | null
    } | null
    options: { name: string; values: string[] }[]
    productionDays: {
        value: number | null
    } | null
    seo?: {
        description: string | null
        title: string | null
    }
    sizeGuideImage: {
        reference?: {
            image: shopify.Image
        }
    } | null
    sizeGuideText: {
        value: string | null
    } | null
    subtitle: {
        value: string | null
    } | null
    totalInventory: number | null
    variants: { edges: { node: Variant }[] }
}

export const PRODUCT_FRAGMENT = fragment`
    fragment BaseProduct on Product {
        ...${MEDIUM_PRODUCT_FRAGMENT}
        deliveryExtra: metafield(namespace: "front", key: "delivery_extra") {
            value
        }
        productionDays: metafield(namespace: "api", key: "production_days") {
            value
        }
        subtitle: metafield(namespace: "front", key: "subtitle") {
            value
        }
        sizeGuideImage: metafield(namespace: "front", key: "size_guide_pic") {
          reference {
            ... on MediaImage {
              image {
                originalSrc
              }
            }
          }
        }
        sizeGuideText: metafield(namespace: "front", key: "size_guide_text") {
            value
        }
        totalInventory
        seo {
            description
            title
        }
        options {
            name
            values
        }
        variants(first: 100) {
            edges {
                node {
                    ...${VARIANT_FRAGMENT}
                }
            }
        }
    }
`

const GET_PRODUCT_QUERY = query<{ node: Product }, { id: string }>`
    query($id: ID!) {
        node(id: $id) {
            ...${PRODUCT_FRAGMENT}
        }
    }
`

export async function get(id: string): Promise<Product | undefined> {
    const result = await request(GET_PRODUCT_QUERY, {
        id
    })

    if (result) {
        return result.node
    }
}

const GET_MEDIUM_PRODUCT_LIST_QUERY = (ids: string[]) =>
    gqlToQuery<Record<string, Product | null>, Record<string, string>>({
        content: `
        query(${ids.map((_id, index) => `$id_${index}: ID!`).join(',')}) {
            ${ids
                .map(
                    (_id, index) => `product_${index}: node(id: $id_${index}) {
                    ...${MEDIUM_PRODUCT_FRAGMENT.name}
                }`
                )
                .join('\n')}
        }
    `,
        fragments: new Set([MEDIUM_PRODUCT_FRAGMENT])
    })

export async function getMediumProductList(ids: string[]): Promise<(Product | null)[]> {
    if (ids.length === 0) {
        return Promise.resolve([])
    }

    const result = await request(
        GET_MEDIUM_PRODUCT_LIST_QUERY(ids),
        ids.reduce((variables, id, index) => {
            variables[`id_${index}`] = id
            return variables
        }, {} as Record<string, string>)
    )

    return Object.values(result)
}

const GET_MEDIUM_PRODUCTS_QUERY = query<
    { products: { edges: { node: Product }[] } },
    { query: string; first: number }
>`
    query getProducts($first: Int, $query: String) {
        products(first: $first, query: $query) {
            edges {
                node {
                    ...${MEDIUM_PRODUCT_FRAGMENT}
                }
            }
        }
    }
`

export async function getMediumProducts(variables?: {
    first: number
    query: string
}): Promise<{ edges: { node: Product }[] }> {
    const result = await request(GET_MEDIUM_PRODUCTS_QUERY, variables, IS_IN_BROWSER)

    return result.products
}
